/**
 * Settings (/settings). Per-user preferences, reached from the avatar menu in
 * the header. Not a catalog tool: it is deliberately absent from
 * COCKPIT_DEPARTMENTS, because it belongs to the signed-in person rather than to
 * a department, and adding it there would trip the Access-console parity test.
 *
 * Structure copied from The Sweet Spot's `src/pages/Settings.tsx`: a centered
 * max-width column, top-left back link, an h1 with a one-line subtitle, and a
 * tab strip. Two tabs: Email reports and Notifications.
 *
 * The Email reports tab lists every report-email subscription the caller holds,
 * across all reports, with the saved filters shown in plain language and a
 * per-row delete. Creating a subscription happens on the report's own page (for
 * example Commerce > Inventory), because that is where the filter state lives.
 *
 * The Notifications tab lists every notification type the caller may subscribe
 * to, with a Teams and an Email switch per type (SMS is greyed, "coming later").
 * Types are grouped by the tool they come from (a "Service Desk" heading beside
 * Cockpit's own types), each with the same switches. A type is off until the
 * person turns it on, except the Service Desk types the migration turned on for
 * the people who most need them. Backend: api#665; the tab degrades to "Not
 * available yet" until that ships.
 */

import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Bell, Mail, X } from 'lucide-react';
import { Pill } from '@/components/Pill';
import {
  filtersLabel,
  useDeleteReportSubscription,
  useReportSubscriptions,
  type ReportSubscription,
} from '@/lib/useReportSubscriptions';
import {
  groupNotificationTypes,
  useNotificationTypes,
  useSetSubscription,
  type NotificationType,
} from '@/lib/useNotifications';

// Tabbed settings shell. Tab one is the report-email list, tab two is the
// notification switches; more tabs (display, etc.) slot in beside them later.
const TABS = [
  { key: 'reports', label: 'Email reports' },
  { key: 'notifications', label: 'Notifications' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/**
 * One subscription row with an inline two-step delete confirm. Resting state is
 * an X icon; tapping it swaps in a red Delete / muted Cancel pair. Pattern from
 * Sweet Spot's Sampling EntryRow, chosen over window.confirm so the confirm
 * stays in the row the user is looking at.
 */
function SubscriptionRow({ sub, isFirst }: { sub: ReportSubscription; isFirst: boolean }) {
  const [confirming, setConfirming] = useState(false);
  const deleteMut = useDeleteReportSubscription();

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 ${
        isFirst ? '' : 'border-t border-amp-line dark:border-amp-dark-line'
      }`}
    >
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amp-cream-warm text-amp-olive dark:bg-amp-dark-surface-warm dark:text-amp-olive-dark">
        <Mail size={15} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-semibold text-amp-ink dark:text-amp-dark-ink">
          {sub.report_label}
        </p>
        <p className="text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {filtersLabel(sub)}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Pill variant="outline" title="How often this report is emailed to you">
          Daily
        </Pill>
        {confirming ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => deleteMut.mutate(sub.id, { onSuccess: () => setConfirming(false) })}
              disabled={deleteMut.isPending}
              className="rounded px-2 py-0.5 text-[12px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60 dark:text-red-300 dark:hover:bg-red-900/20"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded px-2 py-0.5 text-[12px] text-amp-ink-muted hover:bg-amp-cream-warm dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface-warm"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            aria-label={`Stop emailing ${sub.report_label}`}
            title="Stop emailing this report"
            className="rounded p-1 text-amp-ink-muted hover:bg-amp-cream-warm hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface-warm dark:hover:text-amp-dark-ink"
          >
            <X size={14} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

function EmailReports() {
  const { subscriptions, available, isLoading } = useReportSubscriptions();

  if (isLoading) {
    return (
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Loading your subscriptions...
      </p>
    );
  }

  // 404 / 503 / shape mismatch: say so plainly rather than implying the caller
  // has no subscriptions, which would be a different and misleading statement.
  if (!available) {
    return (
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Email report settings are not available right now. Try again shortly.
      </p>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        You have no email report subscriptions yet. Open a report such as{' '}
        <Link
          to="/commerce/inventory"
          className="text-amp-ink underline hover:no-underline dark:text-amp-dark-ink"
        >
          Commerce &gt; Inventory
        </Link>
        , set the filters you want, then use the subscribe control on that page.
      </p>
    );
  }

  return (
    <div>
      <p className="mb-3 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Each subscription emails you one report every morning. The attached sheet contains only the
        rows matching the filters saved with that subscription, so you can hold several of the same
        report with different filters.
      </p>
      <div className="overflow-hidden rounded-lg border border-amp-line dark:border-amp-dark-line">
        {subscriptions.map((sub, i) => (
          <SubscriptionRow key={sub.id} sub={sub} isFirst={i === 0} />
        ))}
      </div>
    </div>
  );
}

const TEAMS_DISABLED_TIP = 'Teams messages need an @ampersandbrands.com account';

/** Small accessible on/off switch. Colors come from the amp tokens. */
function Switch({
  checked,
  onChange,
  label,
  disabled,
  title,
}: {
  checked: boolean;
  onChange?: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={title}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-amp-line transition-colors disabled:cursor-not-allowed disabled:opacity-50 dark:border-amp-dark-line ${
        checked ? 'bg-amp-olive-deep' : 'bg-amp-cream-warm dark:bg-amp-dark-surface-warm'
      }`}
    >
      <span
        aria-hidden
        className={`inline-block h-3.5 w-3.5 rounded-full bg-amp-cream shadow transition-transform ${
          checked ? 'translate-x-[18px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function ChannelCell({ caption, children }: { caption: string; children: ReactNode }) {
  return (
    <div className="flex w-14 flex-col items-center gap-1">
      {children}
      <span className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">{caption}</span>
    </div>
  );
}

function NotificationRow({ type, isFirst }: { type: NotificationType; isFirst: boolean }) {
  const save = useSetSubscription();
  const set = (next: { teams: boolean; email: boolean }) =>
    save.mutate({ code: type.code, ...next });
  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 ${
        isFirst ? '' : 'border-t border-amp-line dark:border-amp-dark-line'
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-semibold text-amp-ink dark:text-amp-dark-ink">{type.label}</p>
        <p className="text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {type.description}
        </p>
        {save.isError && (
          <p role="alert" className="mt-1 text-[13px] text-red-700 dark:text-red-300">
            Could not save that change. It was put back.
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-start gap-1">
        <ChannelCell caption="Teams">
          <Switch
            checked={type.teams && type.can_teams}
            disabled={!type.can_teams}
            title={type.can_teams ? undefined : TEAMS_DISABLED_TIP}
            label={`${type.label}: Teams`}
            onChange={(v) => set({ teams: v, email: type.email })}
          />
        </ChannelCell>
        <ChannelCell caption="Email">
          <Switch
            checked={type.email}
            label={`${type.label}: Email`}
            onChange={(v) => set({ teams: type.teams, email: v })}
          />
        </ChannelCell>
        <ChannelCell caption="SMS">
          <Switch checked={false} disabled label={`${type.label}: SMS (coming later)`} title="Coming later" />
        </ChannelCell>
      </div>
    </div>
  );
}

function Notifications() {
  const { types, available, isLoading } = useNotificationTypes();
  const save = useSetSubscription();

  if (isLoading) {
    return (
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Loading your notification choices...
      </p>
    );
  }

  if (!available) {
    return (
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Not available yet. Notification settings will appear here once they are switched on.
      </p>
    );
  }

  const groups = groupNotificationTypes(types);
  const anyOn = types.some((t) => t.teams || t.email);
  const turnAllOff = () => {
    for (const t of types) {
      if (t.teams || t.email) save.mutate({ code: t.code, teams: false, email: false });
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-start justify-between gap-3">
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Choose how Cockpit and the Service Desk reach you when something happens. A type stays off
          until you turn it on, except some Service Desk notices that were switched on for you at
          launch so nobody misses a reply to their own request. Change any of them here.
        </p>
        <button
          type="button"
          onClick={turnAllOff}
          disabled={!anyOn || save.isPending}
          className="shrink-0 rounded border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm disabled:opacity-50 dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          Turn all off
        </button>
      </div>
      {types.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          <Bell size={14} aria-hidden />
          No notifications are available for your access yet.
        </p>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <section key={g.source} aria-label={g.label}>
              {groups.length > 1 && (
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
                  {g.label}
                </h2>
              )}
              <div className="overflow-hidden rounded-lg border border-amp-line dark:border-amp-dark-line">
                {g.types.map((t, i) => (
                  <NotificationRow key={t.code} type={t} isFirst={i === 0} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

export function SettingsPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<TabKey>(
    searchParams.get('tab') === 'notifications' ? 'notifications' : 'reports',
  );

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Settings</h1>
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Your personal Cockpit preferences.
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Settings sections"
        className="mb-4 flex gap-1 border-b border-amp-line dark:border-amp-dark-line"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${
              tab === t.key
                ? 'border-amp-olive text-amp-ink dark:border-amp-olive-dark dark:text-amp-dark-ink'
                : 'border-transparent text-amp-ink-muted dark:text-amp-dark-ink-muted'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'reports' && <EmailReports />}
      {tab === 'notifications' && <Notifications />}
    </div>
  );
}
